import {
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Animated,
  FlatList,
  Image,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native'
import {useSafeAreaInsets} from 'react-native-safe-area-context'
import {LinearGradient} from 'expo-linear-gradient'
import {Feather} from '@expo/vector-icons'
import {Plural, Trans, useLingui} from '@lingui/react/macro'

import {
  musicChoiceFromRecord,
  resolveBackgroundMusic,
} from '#/lib/backgroundMusic'
import {MUSIC_LIBRARY} from '#/lib/backgroundMusicLibrary'
import {
  duckFlashbookMusic,
  playReadingAudio,
  startFlashbookMusic,
  stopAllFlashbookAudio,
  unduckFlashbookMusic,
} from '#/lib/flashbookAudio'
import {type ClassPlay} from '#/lib/flashbookClassReport'
import {
  bookDirection,
  type FlashBookDirection,
  pageDirection,
} from '#/lib/flashbookDirection'
import {
  readPagerReport,
  SEAT_GRACE_MS,
  type SeatRequest,
} from '#/lib/flashbookPager'
import {resolvePageTurn} from '#/lib/flashbookPageTurn'
import {type FlashBookReadProgress} from '#/lib/flashbookProgress'
import {openFocusEntries} from '#/lib/flashbookReport'
import {
  comparePractice,
  practiceBook,
  type PracticeComparison,
  type PracticeEntry,
  questionAsEntry,
} from '#/lib/flashbookReRead'
import {verdictScrollTop} from '#/lib/flashbookVerdictView'
import {clamp} from '#/lib/numbers'
import {useFlashBookPageAnimation} from '#/state/preferences'
import {useCreateFlashbookPlayMutation} from '#/state/queries/create-flashbook-play'
import {AgentContext} from '#/state/session'
import {useBreakpoints} from '#/alf'
import {type SlideAnswerResult} from '#/components/capsules/play/types'
import * as Dialog from '#/components/Dialog'
import {FlashBookBlockPlayer} from '#/components/flashbooks/FlashBookBlockPlayer'
import {FlashBookClassReport} from '#/components/flashbooks/FlashBookClassReport'
import {FlashBookHtmlPlayer} from '#/components/flashbooks/FlashBookHtmlPlayer'
import {
  FlashBookDivider,
  FlashBookPageOrnament,
  FlashBookPageRuling,
  FlashBookPageTexture,
} from '#/components/flashbooks/FlashBookPageDecor'
import {getFlashBookPlayPalette} from '#/components/flashbooks/FlashBookPlayTheme'
import {FlashBookPracticeResult} from '#/components/flashbooks/FlashBookPracticeResult'
import {FlashBookReport} from '#/components/flashbooks/FlashBookReport'
import {FlashBookReportShareSheet} from '#/components/flashbooks/FlashBookReportShareSheet'
import {useFlashBookProgress} from '#/components/flashbooks/useFlashBookProgress'
import * as Layout from '#/components/Layout'
import {StudioWindowVideo} from '#/components/studio/StudioWindowVideo'
import {
  clampedInkRoom,
  designInHand,
  type FlashBookDesign,
  getFlashbookDesign,
} from '#/themes-flashbook/design'
import {getFlashbookTheme} from '#/themes-flashbook/registry'
import {SLIDE_TYPE_META} from '#/types/capsule'
import {
  type FlashBookModel,
  type FlashBookPageResult,
  type FlashBookThemeTokens,
} from '#/types/flashbook'

type Phase = 'cover' | 'reading' | 'report' | 'practice-result' | 'class-report'

/**
 * The bed this book asks the engine for, read from the record's own field.
 *
 * Three states, three behaviours, and none of them may be guessed at:
 *
 *  · no field at all — nothing was ever chosen, so the theme plays what it always
 *    has; this is every book written before the catalogue existed.
 *  · `'none'`        — the creator chose silence, and it stays silent.
 *  · a track id      — that track is the bed, or silence if the library no longer
 *    holds it (a withdrawn track must not resurrect the theme's music).
 */
function musicFor(recordValue: string | undefined) {
  return resolveBackgroundMusic(
    MUSIC_LIBRARY,
    musicChoiceFromRecord(recordValue),
  )
}

/**
 * A practice in progress: the questions, and the read they came from.
 *
 * The entries are the *baseline* — how each question went last time — and they
 * travel with the run because the comparison is against them and nothing else (see
 * `#/lib/flashbookReRead`).
 */
type PracticeRun = {
  entries: PracticeEntry[]
  /** When the practice began, so its duration is its own and not a guess. */
  startedAt: string
  /**
   * The read whose questions these were — `null` when the reader is not a learner.
   *
   * A preview and a fixture have no read to attach a practice to, and inventing an
   * id would put a practice into a register that has no read for it to belong to.
   * An unfinished read is a read like any other here: it has an id of its own from
   * the moment it begins (see `#/lib/flashbookProgress`), so a practice of its
   * questions belongs to it and is still attached when that read is finished.
   */
  ofSessionId: string | null
  /**
   * Where the practice was started from, and so where its ending returns to.
   *
   * The same screen cannot be the answer for both: a practice of a read that has
   * *ended* belongs to the report — the report is the read's own ending, and the
   * comparison is against the number printed there. A practice of a read that is
   * still going belongs to the cover, because a report of a part-way read is a
   * report of a book the learner has not read.
   */
  origin: 'report' | 'cover'
}

/** How long a themed page turn takes, in milliseconds. */
const FADE_OUT_MS = 130
const FADE_IN_MS = 210

type Props = {
  book: FlashBookModel
  isPreview?: boolean
  previewFooter?: ReactNode
  /** Start directly in reading mode for a focused block/page preview. */
  startInReading?: boolean
  /**
   * Open at the learner's own place, skipping the cover.
   *
   * The reader's version of `startInReading`, and not the same thing: a preview
   * wants page one and no read, while a learner who pressed *Continue* somewhere
   * outside the book wants the page they left, with the answers behind it and the
   * same read still running. So this one *continues* a read rather than beginning
   * one — and when the device holds no place (a book opened and set down on its
   * first page), it does nothing at all and the cover is shown, because a cover with
   * nothing to continue is the honest front door.
   *
   * Asked for by `#/lib/routes/types` (`FlashBookPlay.resume`), which is what a feed
   * card's Continue sends.
   */
  openAtPlace?: boolean
  /**
   * Open on the *class report*, for the book's own author.
   *
   * A teacher checking on their class has no reason to sit the book first, and
   * making them read it to see how the class did made the report a reward for
   * something they had already done. Asked for by `#/lib/routes/types`
   * (`FlashBookPlay.classReport`), which is what a feed card's Class report sends.
   *
   * The name says *asked for*, and the pair of guards is the point: the reader
   * shows it only to the book's author (`isAuthor`), and only when no fixture read
   * is being displayed in its place (`seededRead`). So this is a request the reader
   * may refuse, which is what a param travelling in a public URL has to be.
   */
  openAtClassReport?: boolean
  initialPageIndex?: number
  isSinglePage?: boolean
  /**
   * Whether this reader's place in the book is kept.
   *
   * True for a learner and false for a preview — the studio sampling its own
   * book is not a learner reading it — and a fixture that opens a book on
   * somebody's behalf says so rather than writing a read that never happened.
   */
  trackProgress?: boolean
  /**
   * A read that has already happened, to open the report on.
   *
   * The reader's ending is the one screen a browser cannot reasonably reach on
   * its own: getting there honestly means answering ten different question widgets
   * on nine themes, and the report is worth measuring on all of them. A seeded read
   * lets the fixture open it directly — and it is deliberately *not* saved as a
   * play, because it is a fixture rather than something a learner did.
   */
  seededRead?: {pageResults: FlashBookPageResult[]; elapsedMs: number}
  /**
   * A register seeded by a fixture, for the report's progression across reads.
   *
   * The same seam as `seededRead` and for the same reason: a fixture read is never
   * written to the device (see `trackProgress`), so a report opened over one has no
   * history of its own — and the progression is a screenful of claims a browser
   * should be able to measure rather than take on trust. Undefined everywhere else,
   * where the reader's own register is the only one there is.
   */
  seededProgress?: FlashBookReadProgress | null
  /**
   * Whether the reader is the book's own author, and so has a class.
   *
   * The class report is the *teacher's* ending, and only the person who set the
   * book has a class to report on: everybody else gets the learner's report, which
   * is what they came for. Decided by the caller because that is where the session
   * and the book's author are both known.
   */
  isAuthor?: boolean
  /**
   * The class's plays, supplied directly instead of fetched.
   *
   * A fixture uses this: a browser cannot post plays to an AppView, and the screen
   * worth measuring is the screen that ships, so the plays are handed to it rather
   * than mocked into the network.
   */
  classPlays?: ClassPlay[]
  onClose: () => void
}

/** Book pages are always portrait 9:16 — mobile first, on any screen size. */
const FRAME_ASPECT = 9 / 16
/**
 * Breathing room so a framed book reads as a device rather than filling its
 * column edge to edge. Dropped on narrow columns, where the book is meant to
 * run full-bleed.
 */
const COLUMN_GUTTER = 24

/**
 * How strongly a section break is drawn *on a ruled page*, where it is one line
 * of the ruling rather than a bar across the sheet.
 *
 * Matches the strength the ruling itself is drawn at (see `FlashBookPageRuling`),
 * because on lined paper the break between two exercises must read as the paper
 * and not as a painted band.
 */
const RULED_DIVIDER_OPACITY = 0.18

export function FlashBookPlayer({
  book,
  isPreview = false,
  previewFooter,
  startInReading = false,
  openAtPlace = false,
  openAtClassReport = false,
  initialPageIndex = 0,
  isSinglePage = false,
  trackProgress = !isPreview,
  seededRead,
  seededProgress,
  isAuthor = false,
  classPlays,
  onClose,
}: Props) {
  const {t: l} = useLingui()
  const insets = useSafeAreaInsets()
  const {width: windowWidth, height: windowHeight} = useWindowDimensions()
  const {gtMobile} = useBreakpoints()
  const {mutate: savePlay} = useCreateFlashbookPlayMutation()
  const animationsEnabled = useFlashBookPageAnimation()
  /**
   * Who is reading, for the report's share sheet.
   *
   * Read here and handed *down* as a prop, because the sheet draws through the
   * app's portal on the web — a different React tree, where this reader's
   * providers do not reach. `null` while signed out, which the sheet says out
   * loud rather than offering a post button that cannot post.
   */
  const agent = useContext(AgentContext)
  const shareControl = Dialog.useDialogControl()

  const theme = getFlashbookTheme(book.themeId)
  const tok = theme.tokens
  const design = getFlashbookDesign(theme.id)
  // The same palette the question widgets use, so page furniture (buttons,
  // chips) and the widgets inside agree on ink-on-accent and surface colours.
  const pal = getFlashBookPlayPalette(tok)
  const htmlThemeUrl = book.behaviorSettings?.htmlThemeUrl as string | undefined
  /**
   * The practice being sat, if any.
   *
   * State rather than a mode on the report, because it outlives the report: the
   * practice ends on a screen of its own, and the run has to be over *before* the
   * comparison can be drawn.
   */
  const [practice, setPractice] = useState<PracticeRun | null>(null)
  /**
   * The pages being read — the book, or the book cut down to a practice.
   *
   * A practice is not a second player: it is these two lists being one. Everything
   * below — the pager, the running head, the page furniture, the widgets, the
   * direction, the theme's type — then reads a practice exactly as it reads the
   * book, because to the reader it *is* the book, holding only the questions worth
   * another look (see `#/lib/flashbookReRead`).
   */
  const bookPages = useMemo(
    () =>
      isSinglePage
        ? book.pages.slice(initialPageIndex, initialPageIndex + 1)
        : book.pages,
    [book.pages, isSinglePage, initialPageIndex],
  )
  const pages = useMemo(
    () => (practice ? practiceBook(book, practice.entries).pages : bookPages),
    [book, practice, bookPages],
  )

  /**
   * The learner's place in this book, saved as they go.
   *
   * Off for a preview and off for a seeded fixture read: a browse through a book
   * is not a reading of it, and registering one would put a book nobody read into
   * the learner's history — and offer to resume a book they never started.
   */
  const {
    summary: readSummary,
    // The register itself, for the report's progression across reads: the counts
    // in `readSummary` say how many times the book was read, this says how each
    // one went (see `#/lib/flashbookReadHistory`).
    progress: readProgress,
    resume: readResume,
    begin: beginRead,
    adopt: adoptRead,
    note: noteRead,
    finish: finishRead,
    practice: registerPractice,
  } = useFlashBookProgress({
    bookUri: book.id,
    bookTitle: book.title,
    pages,
    enabled: trackProgress && !seededRead,
    /*
     * The learner's repo, so the place follows them to another device. Handed in
     * rather than read here, because the reader's own state must not depend on the
     * session: a signed-out learner reads, and their place stays on this device
     * (`#/lib/flashbookProgressSync`).
     */
    agent,
  })

  /**
   * The place this player was told to open at, if there is one to open at.
   *
   * Decided once, from the place the *store* held as the player mounted — the same
   * place the cover's own Continue would use, including the reader's rule for when
   * there is nothing to continue at all (see `#/lib/flashbookProgress`). A fixture
   * read and a preview are excluded for the same reason their progress is off: they
   * are not a learner's reading, and there is no place of their own to open at.
   */
  const opensAtPlace =
    openAtPlace && !isPreview && !seededRead ? readResume : null
  /**
   * The page it opens on, clamped to the book as it is *now*.
   *
   * A book can lose pages between two sittings, and the place was written against
   * the book as it was: the pager is handed a page it has, not the one it was told
   * about — which is also what `resumeRead` reconciles a moment later.
   */
  const placePage = opensAtPlace
    ? Math.max(0, Math.min(bookPages.length - 1, opensAtPlace.pageIndex))
    : 0

  /**
   * Whether the book was opened *on* its class report.
   *
   * Both halves are required, and they are the same two a fixture read is kept
   * apart by: only the book's own author has a class (the route is public, so the
   * param can only be a request), and a fixture read is shown in its place when one
   * was handed in. It is decided once, from the mount, because it is *where the
   * reader was opened* — a fact about this visit rather than about the reader.
   */
  const opensOnClassReport = openAtClassReport && isAuthor && !seededRead
  /**
   * Where the class report's way out goes.
   *
   * Two doors lead into that screen and they must not come back through the same
   * one. Opened from the report of the learner's own read, the way out is back to
   * that report — the screen the teacher was reading a moment ago. Opened straight
   * from the book's card, there *is* no such report, and sending them to one would
   * drop a teacher into a report of a read they never did; so the way out is out of
   * the book, which is where they came from.
   */
  const [classReturnTo, setClassReturnTo] = useState<'report' | 'exit'>(
    opensOnClassReport ? 'exit' : 'report',
  )

  const [phase, setPhase] = useState<Phase>(
    seededRead
      ? 'report'
      : opensOnClassReport
        ? 'class-report'
        : startInReading || opensAtPlace
          ? 'reading'
          : 'cover',
  )
  /**
   * The practice's answers, kept apart from the read's.
   *
   * This separation is the feature. Folding a practice's answers back into the
   * read's would recompute the report with a better score and erase the number the
   * comparison is *against* — and a learner who goes back into the book from a
   * question the report named is still reworking one read, which is why the report
   * keeps its own results (see `reviewPage`).
   */
  const [practiceResults, setPracticeResults] = useState<FlashBookPageResult[]>(
    [],
  )
  const [practiceComparison, setPracticeComparison] =
    useState<PracticeComparison | null>(null)
  const [pageIdx, setPageIdx] = useState(
    isSinglePage ? 0 : opensAtPlace ? placePage : initialPageIndex,
  )
  /** Measured height of the paging area — each page's own height. */
  const [pagerHeight, setPagerHeight] = useState(0)
  const [pageResults, setPageResults] = useState<FlashBookPageResult[]>(
    seededRead?.pageResults ?? opensAtPlace?.pageResults ?? [],
  )
  /**
   * How long the read took, fixed the moment the book is finished.
   *
   * Read from the clock *once*, rather than while rendering the report: a
   * duration recomputed on every render would drift as the learner scrolled the
   * report, and would tell them they took longer than they did.
   */
  const [elapsedMs, setElapsedMs] = useState(
    seededRead?.elapsedMs ?? opensAtPlace?.timeMs ?? 0,
  )
  const [coverPlaying, setCoverPlaying] = useState(false)
  /** Whether the cover *film* is making a sound, reported by the player itself. */
  const [coverFilmPlaying, setCoverFilmPlaying] = useState(false)
  /**
   * Which answers the reading screen is measuring — the practice's, or the read's.
   *
   * The running progress measure is "how much of *this* sitting is done", and a
   * practice sitting over questions the read already answered would otherwise open
   * at 100% and never move.
   */
  const activeResults = practice ? practiceResults : pageResults
  /**
   * The read the last finish registered.
   *
   * A practice is registered *against* the read its questions came from, and the
   * id is only known once that read has ended — so it is captured at the finish
   * rather than looked up later, when the register may hold newer reads.
   */
  const finishedReadId = useRef<string | null>(null)

  /*
   * When this sitting began — which for a place opened at once is the read's own
   * clock, not the moment the screen mounted: a continue of a nine-minute read is
   * nine minutes old before it is turned into the tenth.
   */
  const sessionStart = useRef(Date.now() - (opensAtPlace?.timeMs ?? 0))
  const pageEnter = useRef(Date.now())
  const pageTimes = useRef<Record<string, number>>({})
  const blockMounts = useRef<Record<string, number>>({})
  const playSaved = useRef(false)
  const coverAudio = useRef<{stop: () => void} | null>(null)
  const pagerRef = useRef<FlatList<FlashBookModel['pages'][number]> | null>(
    null,
  )
  /**
   * Whether a pager exists to be moved.
   *
   * State rather than only a ref, because the seating has to *run again* when the list
   * appears: the phase change that opens the book and the mount of the pager it renders
   * are one commit, so the effect that asks for a page fires while the list it is asking
   * has not laid out yet. With this, the request is made again the moment there is a
   * pager to hear it.
   */
  const [pagerMounted, setPagerMounted] = useState(false)
  const attachPager = useCallback(
    (node: FlatList<FlashBookModel['pages'][number]> | null) => {
      pagerRef.current = node
      setPagerMounted(node != null)
    },
    [],
  )
  /**
   * Where the reader is, as the pager's own reports have to be judged against them.
   *
   * `#/lib/flashbookPager` holds the rule; this is the seating it judges, plus whether
   * the next move is an *opening* rather than a page turn. An opening is both instant
   * and authoritative while in flight, which is what keeps a continue of a hundred-page
   * book from being pulled back to page one by the pager's own layout.
   */
  const askedPagerFor = useRef<SeatRequest | null>(null)
  /**
   * Whether the next seating is the book *opening* on a page rather than a page being
   * turned. True to begin with: the first seating of a visit is always an opening.
   */
  const openingSeat = useRef(true)
  // The page area's opacity for the `fade` turn.
  const pageFade = useMemo(() => new Animated.Value(1), [])
  /** A page change that already happened by dragging the pager. */
  const movedByReader = useRef(false)
  const fadeInFlight = useRef(false)
  const fadeTarget = useRef<number | null>(null)
  const pageIdxRef = useRef(pageIdx)
  pageIdxRef.current = pageIdx

  /**
   * The theme's page-turn style, or `slide` for themes that predate the token.
   * Readers can switch animations off; the page then flips instantly.
   */
  const pageTurn = useMemo(
    () => resolvePageTurn(tok.pageTurn, animationsEnabled),
    [tok.pageTurn, animationsEnabled],
  )

  useEffect(() => {
    return () => {
      stopAllFlashbookAudio()
    }
  }, [])

  /**
   * Save where the reader is, as they go.
   *
   * On the page *and* on the answers, because the two move independently: turning
   * a page records how far they got, answering records what they did there. Both
   * are cheap — writes are coalesced rather than repeated — and it means a phone
   * that dies between two page turns loses nothing but the page turn.
   *
   * Deliberately only while reading: on the cover there is nothing to save, and
   * after the report the read is closed, where a save is a no-op by design.
   */
  useEffect(() => {
    if (phase !== 'reading') return
    // A practice is not the read's position and must not move it: the read it is
    // about has already ended, and a save here would be a write about a page the
    // learner is not on.
    if (practice) return
    noteRead({
      pageIndex: pageIdx,
      pageResults,
      timeMs: Date.now() - sessionStart.current,
    })
  }, [noteRead, pageIdx, pageResults, phase, practice])

  // 9:16 portrait book, framed and centred on the *web shell's centre column*
  // rather than on the viewport — the same rule the studio follows, so the book
  // you were writing and the book you are reading sit in exactly the same place.
  //
  // Measuring the window instead is wrong in the 1100–1300 band, where the shell
  // shifts its centre column left by `CENTER_COLUMN_OFFSET` (-105px): a
  // viewport-centred book then sits 105px to the right of the column it belongs
  // in. The column is also the width cap, so on a wide window the book grows to
  // fill it instead of stopping at an arbitrary constant.
  const columnWidth = gtMobile ? Layout.CENTER_COLUMN_WIDTH : windowWidth
  const gutter = columnWidth >= 440 ? COLUMN_GUTTER : 0
  const availW = Math.max(240, columnWidth - gutter * 2)
  const availH = Math.max(0, windowHeight - insets.top - insets.bottom)
  const frameW = Math.min(availW, availH * FRAME_ASPECT)
  const frameH = frameW / FRAME_ASPECT
  // A book that leaves room in the column gets the device treatment; one that
  // fills it (phones) stays full-bleed.
  const isFramed = frameW + gutter <= columnWidth

  const settlePageTime = useCallback(
    (fromIdx: number) => {
      const now = Date.now()
      const pageId = pages[fromIdx]?.id
      if (pageId) {
        pageTimes.current[pageId] =
          (pageTimes.current[pageId] ?? 0) + (now - pageEnter.current)
      }
      pageEnter.current = now
    },
    [pages],
  )

  /**
   * Turn to a page the way the theme asks for. The fade is a dip through the
   * page background: fade out, swap while hidden, fade the new page in. A
   * second turn during a fade just retargets it, so the reader never gets stuck
   * on a blank page.
   */
  const goToPage = useCallback(
    (next: number) => {
      const clamped = Math.max(0, Math.min(pages.length - 1, next))
      if (clamped === pageIdxRef.current) return
      settlePageTime(pageIdxRef.current)
      if (pageTurn === 'none') {
        setPageIdx(clamped)
        return
      }
      if (pageTurn === 'fade') {
        fadeTarget.current = clamped
        if (fadeInFlight.current) return
        fadeInFlight.current = true
        Animated.timing(pageFade, {
          toValue: 0,
          duration: FADE_OUT_MS,
          useNativeDriver: true,
        }).start(() => {
          const target = fadeTarget.current
          fadeTarget.current = null
          fadeInFlight.current = false
          if (target != null) setPageIdx(target)
          Animated.timing(pageFade, {
            toValue: 1,
            duration: FADE_IN_MS,
            useNativeDriver: true,
          }).start()
        })
        return
      }
      setPageIdx(clamped)
    },
    [pageFade, pageTurn, pages, settlePageTime],
  )

  /*
   * Move the pager itself: `slide` glides, `fade` and `none` land instantly (the fade
   * happens above, on the page area).
   *
   * `pagerMounted` is in the dependencies because the seating is otherwise asked for in
   * the render that *mounts* the pager, when the ref is attached but the list has not
   * laid out — and a request nobody can hear is a request lost. Keying on the mount as
   * well means the seating is re-issued the moment there is a pager to seat.
   */
  useEffect(() => {
    if (isSinglePage || !pagerMounted) return
    if (movedByReader.current) {
      movedByReader.current = false
      return
    }
    /*
     * An opening is not a turn: the glide is the paper being turned, and it belongs to
     * a turn and to nothing else. Continuing a read, opening a page a report named, the
     * first page of a practice, the first frame after the pager mounts — these are the
     * book *opening* on a page, and gliding to them shows the reader pages they are not
     * on. See `openingSeat`.
     */
    const opening = openingSeat.current
    openingSeat.current = false
    const index = clamp(pageIdx, 0, Math.max(0, pages.length - 1))
    /*
     * Only an *opening* speaks for the pager while it is in flight. A turn does not:
     * the reader who drags a page mid-glide should get the page they dragged to, which
     * is what the pager-report rule has always done.
     */
    askedPagerFor.current = opening
      ? {index, until: Date.now() + SEAT_GRACE_MS}
      : null
    pagerRef.current?.scrollToIndex({
      index,
      animated: !opening && pageTurn === 'slide',
    })
  }, [pageIdx, pageTurn, isSinglePage, pagerMounted, pages.length])

  /**
   * A resized frame moves every page, so the pager has to be re-seated.
   *
   * Armed like an opening, and for the same reason: the resize moves the list under the
   * reader, and the reports that follow the move are the list's own.
   */
  useEffect(() => {
    if (isSinglePage || !pagerMounted) return
    const index = clamp(pageIdxRef.current, 0, Math.max(0, pages.length - 1))
    /*
     * Armed, exactly as an opening is. A resize is the list being moved by the app,
     * not by the reader, so the reports that follow it are the list's own — and
     * without arming, a phone rotating mid-read hands the reader the page the pager
     * happened to report while it re-laid out, which is the reported bug in another
     * costume.
     */
    askedPagerFor.current = {index, until: Date.now() + SEAT_GRACE_MS}
    pagerRef.current?.scrollToIndex({index, animated: false})
  }, [frameW, isSinglePage, pagerMounted, pages.length])

  const handleBlockResult = useCallback(
    (
      pageId: string,
      blockId: string,
      blockType: string,
      r: SlideAnswerResult,
    ) => {
      const mountAt = blockMounts.current[blockId] ?? Date.now()
      const timeSpentMs = r.timeSpentMs ?? Math.max(0, Date.now() - mountAt)
      /*
       * A practice's answers go to the practice's own list, never to the read's —
       * see the note on `practiceResults`. The merge below is the same either way,
       * so it is written once against whichever list is live.
       */
      const write = practice ? setPracticeResults : setPageResults
      write(rs => {
        const existing = rs.find(p => p.pageId === pageId)
        const blockResult = {
          blockId,
          type: blockType,
          isCorrect: r.isCorrect,
          timeSpentMs,
          userAnswer: r.userAnswer,
          points: r.points,
        }
        const mergedBlocks = existing
          ? existing.blocks
              .filter(b => b.blockId !== blockId)
              .concat(blockResult)
          : [blockResult]
        const pageResult: FlashBookPageResult = {
          pageId,
          timeSpentMs: pageTimes.current[pageId] ?? 0,
          blocks: mergedBlocks,
        }
        const next = rs.filter(p => p.pageId !== pageId)
        return [...next, pageResult]
      })
    },
    [practice],
  )

  const startReading = useCallback(() => {
    coverAudio.current?.stop()
    coverAudio.current = null
    setCoverPlaying(false)
    // The book opens on its first page: an opening, not a turn.
    openingSeat.current = true
    beginRead()
    startFlashbookMusic(book.themeId, musicFor(book.backgroundMusicId))
    pageEnter.current = Date.now()
    setPhase('reading')
  }, [beginRead, book.themeId])

  /**
   * Back to where the learner stopped.
   *
   * The answers already given are restored into the player's own state, not just
   * the page: the running progress measure, the answered counts and the report all
   * read from those results, so restoring a position without them would tell a
   * learner that the pages they had already read were untouched — and then ask
   * them to answer the same questions again.
   *
   * The clock resumes rather than restarts, because the read began when it began;
   * and the read keeps its identity, so what is answered after the break belongs to
   * the same read as what came before it.
   */
  const continueReading = useCallback(() => {
    if (!readResume) return
    coverAudio.current?.stop()
    coverAudio.current = null
    setCoverPlaying(false)
    adoptRead(readResume)
    setPageResults(readResume.pageResults)
    setElapsedMs(readResume.timeMs)
    sessionStart.current = Date.now() - readResume.timeMs
    // An opening, not a page turn: the book is being put down on the page the reader
    // left, and nothing is being turned to get there.
    openingSeat.current = true
    setPageIdx(readResume.pageIndex)
    startFlashbookMusic(book.themeId, musicFor(book.backgroundMusicId))
    pageEnter.current = Date.now()
    setPhase('reading')
  }, [adoptRead, book.themeId, readResume])

  /*
   * Open at the place, once, when the way in asked for it.
   *
   * The page, the results and the clock are already what continuing produces — they
   * are set where the player's own state begins, so that the first frame the learner
   * sees is the page they left rather than the cover they were promised would not
   * appear. What is left is what only an effect can do: tell the read it is being
   * adopted (which reconciles the page against the book as it is now and stamps the
   * read as still going), start the book's own music as pressing Continue does, and
   * begin the page's clock.
   *
   * Guarded by a ref rather than by its dependencies, because this is a mount, not a
   * state: a re-render — including one caused by the very write `adoptRead` makes —
   * must not adopt the read a second time.
   */
  const openedAtPlace = useRef(false)
  useEffect(() => {
    if (!opensAtPlace || openedAtPlace.current) return
    openedAtPlace.current = true
    adoptRead(opensAtPlace)
    startFlashbookMusic(book.themeId, musicFor(book.backgroundMusicId))
    pageEnter.current = Date.now()
  }, [adoptRead, book.themeId, opensAtPlace])

  /*
   * The cover film has a voice of its own, so the bed steps back while it plays.
   *
   * The film's transport belongs to the platform — the learner presses the
   * control bar's own play button — so this cannot be inferred from a prop:
   * `StudioWindowVideo` reports what the player is actually doing. Restoring on
   * the way out matters as much as ducking on the way in: a learner who pauses
   * the film, or leaves the cover with it running, must not hand the rest of the
   * book a bed that never comes back.
   */
  useEffect(() => {
    if (!coverFilmPlaying) return undefined
    duckFlashbookMusic()
    return () => unduckFlashbookMusic()
  }, [coverFilmPlaying])

  const finishReading = useCallback(() => {
    settlePageTime(pageIdxRef.current)
    stopAllFlashbookAudio()
    const elapsed = Date.now() - sessionStart.current
    if (!isPreview && !seededRead && !playSaved.current) {
      playSaved.current = true
      const totalPts = pageResults.reduce(
        (a, p) => a + p.blocks.reduce((x, b) => x + b.points, 0),
        0,
      )
      savePlay({
        flashbookUri: book.id,
        score: totalPts,
        timeMs: elapsed,
        pageResults,
      })
    }
    // The place, as well: this is the read's end, so it is registered rather than
    // left open for a continue that will never come.
    finishedReadId.current = finishRead({
      pageResults,
      timeMs: elapsed,
      pageReached: pageIdxRef.current,
    })
    setElapsedMs(elapsed)
    setPractice(null)
    setPhase('report')
  }, [
    book.id,
    finishRead,
    isPreview,
    pageResults,
    savePlay,
    seededRead,
    settlePageTime,
  ])

  /**
   * Back into the book, at a page the report pointed at.
   *
   * The report is the last page of the book, and a report that names the questions
   * to fix without a way to reach them asks the learner to find one page in a
   * hundred by hand. The results already collected are kept — turning back is a
   * visit, not a restart — so returning to the report shows the same read plus
   * whatever was picked up on the way back.
   */
  const reviewPage = useCallback(
    (index: number) => {
      settlePageTime(pageIdxRef.current)
      // Opening the book on a page, not turning to it: the reader was on the report.
      openingSeat.current = true
      setPageIdx(Math.max(0, Math.min(pages.length - 1, index)))
      setPhase('reading')
    },
    [pages.length, settlePageTime],
  )

  /**
   * The questions the read has left open so far, for the cover to offer.
   *
   * A learner who set the book down with three questions wrong has a next move
   * that is not "read the rest of the book": it is those three, and until now the
   * only way to them ran through forty more pages and the report at the end. The
   * list is the report's own rule asked of the part of the book that has been read
   * (see `#/lib/flashbookReport`), so a question on a page the learner has not
   * turned to yet is not "open" — it is unread, and it stays out of the practice.
   *
   * Read from the *open read* rather than from the register, because the register
   * keeps scores and not answers: which questions were missed is a fact the open
   * read holds while it lasts.
   */
  const coverOpenQuestions = useMemo(
    () =>
      readResume
        ? openFocusEntries({
            pages: bookPages,
            pageResults: readResume.pageResults,
          })
        : [],
    [bookPages, readResume],
  )

  /**
   * Sit the questions a report named — only those.
   *
   * The list comes from the report and goes in whole: the practice is not a filter
   * over the book that the player then decides how to apply, it is *these questions*
   * (see `pages`). Everything the read had is left exactly as it was, including the
   * results, because the comparison this run ends on is against them.
   */
  const startPractice = useCallback(
    (entries: PracticeEntry[], origin: 'report' | 'cover' = 'report') => {
      if (entries.length === 0) return
      stopAllFlashbookAudio()
      setPractice({
        entries,
        startedAt: new Date().toISOString(),
        // A practice of the read that has *ended* is registered against the read
        // the report is about; one of the read still going is registered against
        // the open read, which is the read those questions came from.
        ofSessionId:
          origin === 'cover'
            ? (readResume?.sessionId ?? null)
            : finishedReadId.current,
        origin,
      })
      setPracticeResults([])
      setPracticeComparison(null)
      pageTimes.current = {}
      blockMounts.current = {}
      sessionStart.current = Date.now()
      // A practice opens on its first question, as a book does — nothing is turned.
      openingSeat.current = true
      setPageIdx(0)
      startFlashbookMusic(book.themeId, musicFor(book.backgroundMusicId))
      pageEnter.current = Date.now()
      setPhase('reading')
    },
    [book.themeId, readResume],
  )

  /**
   * The end of a practice: what changed, and the practice registered.
   *
   * The comparison is computed from the run's own baseline and results, so it can
   * be drawn even when nothing is registered — a preview practises and sees what
   * changed without any of it being written down as something a learner did.
   */
  const finishPractice = useCallback(() => {
    if (!practice) return
    settlePageTime(pageIdxRef.current)
    stopAllFlashbookAudio()
    const elapsed = Date.now() - sessionStart.current
    const comparison = comparePractice({
      entries: practice.entries,
      pages,
      results: practiceResults,
    })
    if (!isPreview && practice.ofSessionId) {
      registerPractice({
        startedAt: practice.startedAt,
        pages,
        results: practiceResults,
        ofSessionId: practice.ofSessionId,
        fixed: comparison.fixed.length,
        timeMs: elapsed,
      })
    }
    setPracticeComparison(comparison)
    setPhase('practice-result')
  }, [
    isPreview,
    pages,
    practice,
    practiceResults,
    registerPractice,
    settlePageTime,
  ])

  /** The end of whatever is being read: the practice's, or the book's. */
  const finishCurrent = useCallback(() => {
    if (practice) {
      finishPractice()
      return
    }
    finishReading()
  }, [finishPractice, finishReading, practice])

  const onViewableItemsChanged = useCallback(
    ({viewableItems}: {viewableItems: {index: number | null}[]}) => {
      if (isSinglePage) return
      /*
       * What this report is allowed to mean is `#/lib/flashbookPager`'s question, and
       * it is asked rather than re-decided here: the pager reports the pages it moves
       * *through* as well as the one it settles on, so a report is not the reader by
       * default — a seating the app asked for has to be answered before the reader's
       * own movement can be believed. See `askedPagerFor`.
       */
      const read = readPagerReport({
        index: viewableItems[0]?.index ?? null,
        requested: askedPagerFor.current,
        current: pageIdxRef.current,
        now: Date.now(),
      })
      askedPagerFor.current = read.requested
      if (read.reseat != null) {
        /*
         * The list moved off the page it was asked for while it was being seated.
         * The reader's page stands, and the list is put back on it — without
         * animating, because the glide is a page being turned and nothing has been
         * turned here.
         */
        pagerRef.current?.scrollToIndex({index: read.reseat, animated: false})
        return
      }
      if (read.follow == null) return
      // The pager is already there — only the bookkeeping needs to catch up.
      movedByReader.current = true
      settlePageTime(pageIdxRef.current)
      setPageIdx(read.follow)
    },
    [isSinglePage, settlePageTime],
  )
  const viewabilityConfig = useRef({itemVisiblePercentThreshold: 60}).current

  // The book *is* the theme's paper, edge to edge: the running head, the page
  // title, the running page number, the content and the page navigation are all
  // printed on it. The theme's `bg` is only the desk the framed book rests on,
  // so on a phone — where the frame fills the screen — the reader sees nothing
  // but the book.
  /**
   * Which way the book, and the page being read, are written.
   *
   * A page is written the way its own blocks are written, and falls back to the
   * book only when it has no writing of its own to judge by — a plate, a cover
   * image, a page of pure figures. The running head, the page number, the
   * progress measure and the page navigation are all printed *for* the page, so
   * they follow the page: an Arabic page gets Arabic furniture even inside a
   * book that is otherwise in French.
   *
   * Computed up here with the rest of the book's derived state rather than in
   * the reading phase below, because the phases return early and a hook after a
   * return is a hook that does not always run.
   */
  const bookDir = useMemo(() => bookDirection(pages), [pages])
  const pageDir = useMemo(
    () => pageDirection(pages[pageIdx]?.blocks ?? []) ?? bookDir,
    [pages, pageIdx, bookDir],
  )
  const rtl = pageDir === 'rtl'

  /**
   * The design as the *book* is written.
   *
   * A theme names one face per written role, and on a device a face is a single
   * family with no per-glyph fallback — so a role has to name the script it is
   * about to print. The cover is the book's own front matter, so it takes the
   * book's direction. Everything else that prints has a finer one: the page's
   * furniture is resolved per page inside the pager, and a block's text per
   * block, because a block is the smallest thing a creator writes.
   *
   * The same object comes back when the writing does not ask for a different
   * hand (which is always, on the web), so nothing about an English book — or an
   * Arabic one read on the web — changes shape here.
   */
  const bookDesign = useMemo(
    () => designInHand(design, bookDir),
    [design, bookDir],
  )

  /**
   * Ink room for the design's *clamped* furniture — the cover title.
   *
   * `numberOfLines` clamps by clipping, so a heading written in a hand loses the
   * bottom of its descenders unless its box is taller than its leading; see
   * `clampedInkRoom`. Worked out here rather than inline in the JSX because the
   * React Compiler declines to compile this component around an opaque call in a
   * render — and a bail-out there would turn a typographic fix into a lint
   * failure. It follows the *resolved* role, because the two bundled hands do not
   * draw the same height and the room is for the one that will print.
   */
  const inkRoom = {
    coverTitle: clampedInkRoom(bookDesign.type.display),
  }

  /**
   * The direction, as a style the renderer actually applies.
   *
   * Deliberately built per render rather than declared in `StyleSheet.create`:
   * React Native Web compiles a static style to an atomic class and *drops*
   * `direction` on the way — there is no `r-direction-*` rule on the page and no
   * inline style either — while a style object built here reaches the DOM as
   * written. A direction that silently disappears is a book that stays mirrored
   * English with nothing to show for it, so the mechanism is pinned in one place
   * and held by `flashbook-direction.spec.ts`.
   */
  const mirrored = rtl ? ({direction: 'rtl'} as const) : null
  const textMirrored = rtl
    ? ({textAlign: 'right', writingDirection: 'rtl'} as const)
    : null

  const frameShell = [styles.frameShell, {backgroundColor: tok.bg}]
  const frameBox = [
    styles.frame,
    {width: frameW, height: frameH, backgroundColor: tok.pageBg},
    isFramed && [styles.frameRounded, {borderColor: tok.pageBorder}],
  ]

  // ── Cover phase ─────────────────────────────────────────────────────────────
  if (phase === 'cover') {
    const hasArt = !!(book.cover.imageUri || book.cover.videoUri)
    return (
      <View style={frameShell}>
        {/* `Layout.Center` is what makes the book track the web shell's centre
            column (width cap *and* the tablet offset) instead of the viewport. */}
        <Layout.Center style={styles.frameColumn}>
          <View style={frameBox}>
            {/* Full-bleed cover art: the cover *is* the screen */}
            {book.cover.kind === 'video' && book.cover.videoUri ? (
              <StudioWindowVideo
                uri={book.cover.videoUri}
                // The minute the creator chose: a cover that ran on past its
                // window would show the learner a different film from the one
                // the creator trimmed.
                startMs={book.cover.videoStartMs}
                onEnded={() => setCoverPlaying(false)}
                /*
                 * Audible, but only because the learner asked.
                 *
                 * `muted` defaults to true so no *preview* can talk over a
                 * creator, which is right in the studio and wrong here: this is
                 * the cover of the book, nothing plays until the learner presses
                 * play on it, and a press is exactly the gesture the browser
                 * wants before it allows a sound.
                 */
                muted={false}
                onPlayingChange={setCoverFilmPlaying}
                style={StyleSheet.absoluteFill}
              />
            ) : book.cover.imageUri ? (
              <Image
                source={{uri: book.cover.imageUri}}
                style={StyleSheet.absoluteFill}
                resizeMode="cover"
                accessibilityIgnoresInvertColors
              />
            ) : (
              <View
                style={[
                  StyleSheet.absoluteFill,
                  styles.coverFiller,
                  {backgroundColor: tok.bgSecondary},
                ]}>
                <Feather name="book-open" size={64} color={pal.chromeMuted} />
              </View>
            )}

            {/* Scrims: the title and description sit on the art and must stay
              readable on any cover, light or dark. */}
            {hasArt && (
              <>
                <LinearGradient
                  pointerEvents="none"
                  colors={['rgba(0,0,0,0.6)', 'rgba(0,0,0,0)']}
                  style={styles.coverTopScrim}
                />
                <LinearGradient
                  pointerEvents="none"
                  colors={[
                    'rgba(0,0,0,0)',
                    'rgba(0,0,0,0.6)',
                    'rgba(0,0,0,0.95)',
                  ]}
                  locations={[0, 0.42, 1]}
                  style={styles.coverBottomScrim}
                />
              </>
            )}

            <View style={styles.coverTop} pointerEvents="none">
              <Text
                style={[
                  styles.coverKicker,
                  !hasArt && {color: pal.chromeMuted, textShadowRadius: 0},
                ]}>
                <Trans>Interactive book</Trans>
              </Text>
            </View>

            {/* Title + description over the art, then the call to action */}
            <View style={styles.coverBottom}>
              <Text
                style={[
                  styles.coverTitle,
                  bookDesign.type.display,
                  !hasArt && {
                    color: pal.chromeText,
                    textShadowRadius: 0,
                  },
                  // Clamped at three lines: the same ink room as the page's own
                  // heading, for the same reason.
                  inkRoom.coverTitle,
                ]}
                numberOfLines={3}>
                {book.title}
              </Text>
              {!!book.description && (
                <Text
                  style={[
                    styles.coverDesc,
                    !hasArt && {
                      color: pal.chromeMuted,
                      textShadowRadius: 0,
                    },
                  ]}
                  numberOfLines={4}>
                  {book.description}
                </Text>
              )}

              {book.cover.kind === 'audio' && book.cover.audioUri && (
                <TouchableOpacity
                  accessibilityRole="button"
                  onPress={() => {
                    if (coverPlaying) {
                      coverAudio.current?.stop()
                      coverAudio.current = null
                      setCoverPlaying(false)
                    } else {
                      setCoverPlaying(true)
                      coverAudio.current = playReadingAudio(
                        book.cover.audioUri!,
                        {
                          onEnded: () => {
                            coverAudio.current = null
                            setCoverPlaying(false)
                          },
                        },
                      )
                    }
                  }}
                  style={[
                    styles.coverAudioBtn,
                    {backgroundColor: tok.accent, borderColor: tok.cardBorder},
                  ]}>
                  <Feather
                    name={coverPlaying ? 'pause' : 'headphones'}
                    size={18}
                    color={pal.onAccent}
                  />
                  <Text style={[styles.coverAudioTxt, {color: pal.onAccent}]}>
                    {coverPlaying ? l`Pause intro` : l`Play intro audio`}
                  </Text>
                </TouchableOpacity>
              )}

              {/* What has been read of this book, before the choice of what to do
                  about it. A book read over days is one the learner needs
                  reminding they are part-way through, not one they need the title
                  of again. */}
              {readSummary && (
                <Text
                  testID="flashbook-read-history"
                  style={styles.coverHistory}
                  numberOfLines={1}>
                  {readSummary.reads > 0 ? (
                    <>
                      <Plural
                        value={readSummary.reads}
                        one="# read"
                        other="# reads"
                      />
                      {readSummary.bestPct != null &&
                        l` · best ${readSummary.bestPct}%`}
                    </>
                  ) : (
                    <Trans>Started, not finished</Trans>
                  )}
                </Text>
              )}

              {/* The primary action whenever there is a read to continue: a book
                  of a hundred pages is picked back up far more often than it is
                  begun, and it is the press that has to be easiest to find. */}
              {readResume && (
                <TouchableOpacity
                  testID="flashbook-continue"
                  accessibilityRole="button"
                  accessibilityLabel={l`Continue reading`}
                  accessibilityHint={l`Returns to page ${readResume.pageIndex + 1} of ${readResume.pageCount}`}
                  onPress={continueReading}
                  style={[
                    styles.startBtn,
                    {
                      backgroundColor: tok.accent,
                      borderRadius: design.block.radius + 8,
                    },
                  ]}>
                  <Feather name="play" size={18} color={pal.onAccent} />
                  <Text
                    style={[styles.startTxt, {color: pal.onAccent}]}
                    numberOfLines={1}>
                    {l`Continue · page ${readResume.pageIndex + 1} of ${readResume.pageCount}`}
                  </Text>
                </TouchableOpacity>
              )}

              {/*
                The questions this read has left open — offered on the cover.

                Sits between "continue" and "start over" because that is what it
                is: the smaller commitment than reading forty more pages, and the
                larger one than setting the book down. A learner who has three
                questions wrong and no way to face them except through the rest of
                the book is being asked to do the work twice.
              */}
              {coverOpenQuestions.length > 0 && (
                <TouchableOpacity
                  testID="flashbook-cover-practice"
                  accessibilityRole="button"
                  accessibilityHint={l`Asks only the questions not settled yet`}
                  onPress={() => startPractice(coverOpenQuestions, 'cover')}
                  style={[styles.startBtnQuiet, {borderColor: tok.accent}]}>
                  <Feather name="edit-3" size={16} color={pal.chromeText} />
                  <Text
                    testID="flashbook-cover-practice-label"
                    style={[styles.startTxt, {color: pal.chromeText}]}
                    numberOfLines={1}>
                    <Plural
                      value={coverOpenQuestions.length}
                      one="Practise the question still open"
                      other="Practise the # questions still open"
                    />
                  </Text>
                </TouchableOpacity>
              )}

              <TouchableOpacity
                testID="flashbook-start"
                accessibilityRole="button"
                onPress={startReading}
                style={[
                  readResume ? styles.startBtnQuiet : styles.startBtn,
                  {
                    backgroundColor: readResume ? 'transparent' : tok.accent,
                    borderColor: tok.accent,
                    borderRadius: design.block.radius + 8,
                  },
                ]}>
                <Feather
                  name={readResume ? 'rotate-ccw' : 'book-open'}
                  size={18}
                  color={readResume ? '#fff' : pal.onAccent}
                />
                <Text
                  style={[
                    styles.startTxt,
                    {color: readResume ? '#fff' : pal.onAccent},
                  ]}>
                  {readResume ? (
                    <Trans>Start over</Trans>
                  ) : (
                    <Trans>Start reading</Trans>
                  )}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </Layout.Center>
      </View>
    )
  }

  // ── Report phase ────────────────────────────────────────────────────────────
  if (phase === 'report') {
    return (
      <View style={frameShell}>
        {/* `Layout.Center` is what makes the book track the web shell's centre
            column (width cap *and* the tablet offset) instead of the viewport. */}
        <Layout.Center style={styles.frameColumn}>
          <View style={frameBox}>
            <FlashBookReport
              bookTitle={book.title}
              // The *book*, never `pages`: the report is about the read, and a
              // practice in hand must not be able to reshape it.
              pages={bookPages}
              pageResults={pageResults}
              elapsedMs={elapsedMs}
              // This read is already in the register by the time the report draws,
              // so the count includes it.
              readNumber={readSummary?.reads}
              practiceCount={readSummary?.practices}
              // The reader's own register, or the one a fixture seeded for it.
              readProgress={readProgress ?? seededProgress ?? null}
              themeTokens={tok}
              design={design}
              direction={bookDir}
              onReviewPage={reviewPage}
              onPractise={startPractice}
              onShare={isPreview ? undefined : () => shareControl.open()}
              // The teacher's other ending, offered only to the person who set the
              // book. Shown rather than hidden when there is nothing yet: "nobody
              // has played this" is exactly what a teacher wants to know.
              onOpenClassReport={
                isAuthor
                  ? () => {
                      // Entered from the report, so the way out is back to it.
                      setClassReturnTo('report')
                      setPhase('class-report')
                    }
                  : undefined
              }
              onReplay={() => {
                playSaved.current = false
                setPageResults([])
                pageTimes.current = {}
                blockMounts.current = {}
                sessionStart.current = Date.now()
                setElapsedMs(0)
                setPractice(null)
                setPracticeResults([])
                setPracticeComparison(null)
                setPhase('cover')
              }}
              onDone={onClose}
              footer={isPreview ? (previewFooter ?? null) : undefined}
            />
          </View>
        </Layout.Center>
        {/*
          The share sheet lives with the report, not with the player: it draws the
          report, it is opened from the report, and a dialog mounted where the
          screen it belongs to is not on display would be a sheet nobody could
          close. Mounted whether or not it is open, because a dialog control can
          only open a dialog that is already on the screen.
        */}
        <FlashBookReportShareSheet
          control={shareControl}
          agent={agent}
          bookTitle={book.title}
          // The book, never `pages`: the card is the report of the *read*.
          pages={bookPages}
          pageResults={pageResults}
          elapsedMs={elapsedMs}
          themeTokens={tok}
          design={design}
          direction={bookDir}
          readNumber={readSummary?.reads}
        />
      </View>
    )
  }

  // ── Class report (the teacher's ending) ─────────────────────────────────────
  if (phase === 'class-report') {
    return (
      <View style={frameShell}>
        <Layout.Center style={styles.frameColumn}>
          <View style={frameBox}>
            <FlashBookClassReport
              bookTitle={book.title}
              flashbookUri={book.id}
              // The whole book, always: a question's difficulty is a fact about
              // the book, and a practice in hand must not shrink the class's
              // question list to the eight questions one reader had open.
              pages={bookPages}
              plays={classPlays}
              themeTokens={tok}
              design={design}
              direction={bookDir}
              /*
               * The way out, which depends on the way in — see `classReturnTo`.
               * Out of the book when the report was opened straight from a card,
               * because there is no report of a read to go back to.
               */
              returnTo={classReturnTo}
              onClose={() => {
                if (classReturnTo === 'exit') onClose()
                else setPhase('report')
              }}
            />
          </View>
        </Layout.Center>
      </View>
    )
  }

  // ── Practice result ─────────────────────────────────────────────────────────
  if (phase === 'practice-result' && practiceComparison) {
    // The run that was just sat. A result with no run behind it cannot happen —
    // they are set together — but the screen is a screen, and the report is the
    // reading that every practice is ultimately a second look at.
    const origin = practice?.origin ?? 'report'
    return (
      <View style={frameShell}>
        {/* `Layout.Center` is what makes the book track the web shell's centre
            column (width cap *and* the tablet offset) instead of the viewport. */}
        <Layout.Center style={styles.frameColumn}>
          <View style={frameBox}>
            <FlashBookPracticeResult
              bookTitle={book.title}
              comparison={practiceComparison}
              themeTokens={tok}
              design={design}
              direction={bookDir}
              // The questions that are still open, asked again — with the practice
              // just sat as the thing they are compared against next time.
              onPractiseAgain={
                practiceComparison.remaining.length > 0
                  ? () =>
                      startPractice(
                        practiceComparison.remaining.map(questionAsEntry),
                      )
                  : undefined
              }
              /*
               * Where the practice was started from, which is where it goes back
               * to. A practice of an *ended* read belongs to the report; one of a
               * read still going belongs to the cover, and sending that learner to
               * a report of a book they have not read would be the app inventing a
               * verdict out of the pages they did read.
               */
              backTo={origin}
              onBack={() => {
                /*
                 * Leaving the practice *ends it*, and that is load-bearing rather
                 * than tidy: `pages` is the practice's while a practice is in hand,
                 * so leaving it set would rebuild the read's report over the eight
                 * questions just practised — a report of `5/60` in place of the
                 * book's `75/130`, which is exactly the first score this screen
                 * exists to preserve. The comparison itself stays in state, so
                 * nothing on the way back is lost.
                 */
                setPractice(null)
                setPhase(origin === 'cover' ? 'cover' : 'report')
              }}
              onDone={onClose}
            />
          </View>
        </Layout.Center>
      </View>
    )
  }

  // ── Reading phase (HTML theme) ─────────────────────────────────────────────
  if (phase === 'reading' && htmlThemeUrl) {
    return (
      <View style={frameShell}>
        {/* `Layout.Center` is what makes the book track the web shell's centre
            column (width cap *and* the tablet offset) instead of the viewport. */}
        <Layout.Center style={styles.frameColumn}>
          <View style={frameBox}>
            <FlashBookHtmlPlayer
              book={book}
              pages={pages}
              onBlockResult={handleBlockResult}
              onComplete={finishReading}
              onClose={onClose}
            />
          </View>
        </Layout.Center>
      </View>
    )
  }

  // ── Reading phase ───────────────────────────────────────────────────────────
  const answeredCount = pages.reduce(
    (a, p) =>
      a +
      p.blocks.filter(b => {
        const pr = activeResults.find(r => r.pageId === p.id)
        return pr?.blocks.some(br => br.blockId === b.id)
      }).length,
    0,
  )
  const totalBlocks = pages.reduce((a, p) => a + p.blocks.length, 0)
  const progressPct = totalBlocks > 0 ? (answeredCount / totalBlocks) * 100 : 0

  return (
    <View style={frameShell}>
      <Layout.Center style={styles.frameColumn}>
        <View style={frameBox}>
          {/* The theme's paper covers the whole screen — the running head and the
              page navigation are printed on it, not placed on app chrome around
              it. Only the *field* texture goes here; the ruling has to travel
              with the text, so it is drawn inside the scrolling content. */}
          <FlashBookPageTexture
            texture={design.page.texture}
            color={tok.accent}
          />

          {/* Header — the book's running head: title, progress, way out, set in
              the page's own ink and closed by a printed rule.

              Printed *for the page*, so it takes the page's direction: in an
              Arabic book the way out belongs at the right-hand end where the
              reader starts, not mirrored into the corner an English reader
              would reach for. */}
          <View
            style={[
              styles.header,
              {borderBottomColor: pal.paperHairline},
              mirrored,
            ]}>
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={l`Close book`}
              accessibilityHint={l`Leaves the book and returns to where you started`}
              onPress={onClose}
              style={styles.headerBtn}>
              <Feather name="x" size={20} color={pal.muted} />
            </TouchableOpacity>
            <View style={styles.headerCenter}>
              <Text
                style={[styles.headerTitle, textMirrored, {color: pal.text}]}
                numberOfLines={1}>
                {/*
                  A practice says so, in the one place a learner looks to check
                  what they are in: the running head. Without it the screen is
                  indistinguishable from the book, and the eight questions arrive
                  looking like a book that has gone strangely short.
                */}
                {practice
                  ? l`Re-read · ${practice.entries.length} questions`
                  : book.title}
              </Text>
              <FlashBookProgress
                style={design.progress.style}
                pct={progressPct}
                pageIndex={pageIdx}
                totalPages={pages.length}
                accent={tok.accent}
                track={pal.paperTrack}
                rtl={rtl}
              />
            </View>
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={
                practice ? l`See what changed` : l`Finish book`
              }
              accessibilityHint={
                practice
                  ? l`Ends the practice and compares it with the first try`
                  : l`Ends the book and shows your report`
              }
              onPress={finishCurrent}
              testID="flashbook-read-finish"
              style={[
                styles.finishBtn,
                {
                  borderColor: pal.paperHairline,
                  borderRadius: design.block.mode === 'open' ? 4 : 20,
                },
              ]}>
              <Text style={[styles.finishTxt, {color: pal.muted}]}>
                {practice ? l`See what changed` : l`Finish`}
              </Text>
            </TouchableOpacity>
          </View>

          {/* Pages. The themed page turn animates this area — see `pageTurn`. */}
          <Animated.View
            style={[styles.fill, {opacity: pageFade}]}
            // The pager's height is measured, not assumed: the list's own cell
            // wrappers size to their content, so a page styled `flex: 1` inside
            // them would resolve against the content rather than against the
            // book — and a page that is taller than the book never scrolls.
            onLayout={e => {
              const h = Math.round(e.nativeEvent.layout.height)
              setPagerHeight(prev => (prev === h ? prev : h))
            }}>
            <FlatList
              ref={attachPager}
              // The pager must take the frame's remaining height, not its
              // content's: without this the horizontal list grows to its tallest
              // page, every page stretches to that grown height, and each page's
              // own scroller is handed a height larger than the frame — so the
              // page never scrolls *and* the rest of it is clipped by the frame.
              style={styles.fill}
              data={pages}
              keyExtractor={p => p.id}
              horizontal
              pagingEnabled
              showsHorizontalScrollIndicator={false}
              onViewableItemsChanged={onViewableItemsChanged}
              viewabilityConfig={viewabilityConfig}
              // The pager is remounted by every phase change, so the index it
              // opens at is the *current* page — which is what makes the report's
              // "open this page" land on the page it names rather than on the one
              // the book was entered at.
              initialScrollIndex={isSinglePage ? 0 : pageIdx}
              onScrollToIndexFailed={({index}) => {
                // Only hit if the frame was measured mid-flight; land instantly.
                pagerRef.current?.scrollToOffset({
                  offset: index * frameW,
                  animated: false,
                })
              }}
              getItemLayout={(_, index) => ({
                length: frameW,
                offset: frameW * index,
                index,
              })}
              renderItem={({item, index}) => {
                /**
                 * This page's own direction, and the design in it.
                 *
                 * A neighbouring page is mounted a page-width away and is laid
                 * out for itself, not for the page being read: an Arabic page
                 * kept to either side of an English one still prints its own
                 * heading in an Arabic hand.
                 */
                const itemDir = pageDirection(item.blocks) ?? bookDir
                const itemDesign = designInHand(design, itemDir)
                return (
                  <View
                    // The page as a whole, so the layout suite can grade the
                    // *page* — its furniture and the blocks printed on it — and not
                    // only the blocks inside it.
                    testID={`flashbook-play-page--${index}`}
                    style={[
                      styles.page,
                      {width: frameW, height: pagerHeight || undefined},
                    ]}>
                    <View
                      style={[
                        styles.pageTop,
                        {paddingHorizontal: design.page.padding},
                      ]}>
                      {/* The mark that opens the page — chapter rule, masthead
                      ribbon, mission star. Set by the theme, drawn by the page. */}
                      <FlashBookPageOrnament
                        ornament={design.page.ornament}
                        color={tok.accent}
                      />
                      <View style={[styles.pageTopRow, mirrored]}>
                        {!!item.title && (
                          <Text
                            style={[
                              itemDesign.type.title,
                              styles.pageTitleFlex,
                              textMirrored,
                              {color: pal.text},
                              // The heading is clamped, so a hand needs the room
                              // its leading withholds.
                              clampedInkRoom(itemDesign.type.title),
                            ]}
                            numberOfLines={2}>
                            {item.title}
                          </Text>
                        )}
                        {/* Running head: the accent carries the book's identity
                        onto the page furniture, not only the widgets. */}
                        <View
                          style={[
                            styles.pageNumChip,
                            {
                              backgroundColor: tok.accent + '1f',
                              borderColor: tok.accent + '44',
                            },
                          ]}>
                          <Text
                            style={[
                              itemDesign.type.caption,
                              {color: tok.accent},
                            ]}>
                            {index + 1} / {pages.length}
                          </Text>
                        </View>
                      </View>
                    </View>

                    {/* The page *is* the frame: the theme's paper already covers
                      the whole screen, so the content region adds no surface of
                      its own — no sheet, no second border. Blocks print straight
                      onto the page, which is what makes a book read as a book
                      rather than as cards on a coloured background. */}
                    <View style={styles.pageSheet}>
                      <FlashBookPageBlocks
                        blocks={item.blocks}
                        tokens={tok}
                        design={itemDesign}
                        direction={itemDir}
                        onBlockResult={(blockId, type, r) =>
                          handleBlockResult(item.id, blockId, type, r)
                        }
                        onBlockMount={blockId => {
                          if (!blockMounts.current[blockId]) {
                            blockMounts.current[blockId] = Date.now()
                          }
                        }}
                      />
                    </View>

                    <View
                      style={[
                        styles.pageFooter,
                        {borderTopColor: pal.paperHairline},
                        mirrored,
                      ]}>
                      <TouchableOpacity
                        accessibilityRole="button"
                        accessibilityLabel={l`Previous page`}
                        accessibilityHint={l`Goes back one page`}
                        disabled={index === 0}
                        onPress={() => goToPage(index - 1)}
                        style={[
                          styles.navBtn,
                          {borderColor: pal.paperHairline},
                          index === 0 && {opacity: 0.4},
                        ]}>
                        {/* A chevron is not a word: it points at the page it
                          opens. Going back is leftwards only while the reader
                          reads left to right. */}
                        <Feather
                          name={rtl ? 'chevron-right' : 'chevron-left'}
                          size={18}
                          color={pal.muted}
                        />
                        <Text style={[styles.navTxt, {color: pal.muted}]}>
                          <Trans>Back</Trans>
                        </Text>
                      </TouchableOpacity>
                      {index < pages.length - 1 ? (
                        <TouchableOpacity
                          accessibilityRole="button"
                          accessibilityLabel={l`Next page`}
                          accessibilityHint={l`Moves to the next page`}
                          onPress={() => goToPage(index + 1)}
                          style={[
                            styles.nextBtn,
                            {backgroundColor: tok.accent},
                          ]}>
                          <Text style={[styles.nextTxt, {color: pal.onAccent}]}>
                            <Trans>Next page</Trans>
                          </Text>
                          <Feather
                            name={rtl ? 'chevron-left' : 'chevron-right'}
                            size={18}
                            color={pal.onAccent}
                          />
                        </TouchableOpacity>
                      ) : (
                        <TouchableOpacity
                          accessibilityRole="button"
                          accessibilityLabel={
                            practice ? l`See what changed` : l`Finish book`
                          }
                          accessibilityHint={
                            practice
                              ? l`Ends the practice and compares it with the first try`
                              : l`Ends the book and shows your report`
                          }
                          onPress={finishCurrent}
                          testID="flashbook-read-last-page-action"
                          style={[
                            styles.nextBtn,
                            {backgroundColor: tok.accent},
                          ]}>
                          <Text style={[styles.nextTxt, {color: pal.onAccent}]}>
                            {practice ? (
                              l`See what changed`
                            ) : (
                              <Trans>Finish book</Trans>
                            )}
                          </Text>
                          <Feather
                            name="check"
                            size={18}
                            color={pal.onAccent}
                          />
                        </TouchableOpacity>
                      )}
                    </View>
                  </View>
                )
              }}
            />
          </Animated.View>
        </View>
      </Layout.Center>
    </View>
  )
}

function FlashBookPageBlocks({
  blocks,
  tokens,
  design,
  direction,
  onBlockResult,
  onBlockMount,
}: {
  blocks: FlashBookModel['pages'][number]['blocks']
  tokens: FlashBookThemeTokens
  design: FlashBookDesign
  /** The page's own direction — the fallback for a block with no writing. */
  direction: FlashBookDirection
  onBlockResult: (blockId: string, type: string, r: SlideAnswerResult) => void
  onBlockMount: (blockId: string) => void
}) {
  const scrollRef = useRef<ScrollView>(null)
  const mounted = useRef<Record<string, boolean>>({})
  /** The measured content box — the ruling is sized from it, not from guesses. */
  const [content, setContent] = useState({w: 0, h: 0})
  /** Measured height of each block's content, for on-grid rhythm. */
  const [heights, setHeights] = useState<Record<string, number>>({})
  /** Measured y of each block inside the scrolling page, for paging through it. */
  const [offsets, setOffsets] = useState<Record<string, number>>({})
  /** The frame the page is read in — needed to know what "in view" means. */
  const [viewport, setViewport] = useState({w: 0, h: 0})
  /**
   * Where the reader has scrolled to.
   *
   * A ref rather than state: the verdict rule is the only thing that reads it, and
   * it is read at the moment of an answer. Making it state would re-render the whole
   * page on every frame of every scroll.
   */
  const scrolled = useRef(0)

  /**
   * Where the page sits once a question is answered: on the verdict.
   *
   * A book is one continuous page — the next question is simply *below* this one,
   * so nothing has to bring it into view, and there is no advance control on a
   * question at all. What the reader needs after answering is the *verdict*: the
   * tick or the cross, the solution, the explanation, all printed under the
   * question they just answered. Moving them on to the next question scrolls that
   * off the top, so the page had it right and the reader never saw it.
   *
   * The rule is `#/lib/flashbookVerdictView`: move as little as possible, and only
   * ever far enough to put the answered block's own end on screen.
   */
  const scrollToVerdict = useCallback(
    (id: string) => {
      const top = offsets[id]
      const height = heights[id]
      if (top === undefined || height === undefined) return
      scrollRef.current?.scrollTo({
        y: verdictScrollTop({
          blockTop: top,
          blockHeight: height,
          viewportHeight: viewport.h,
          current: scrolled.current,
          padding: design.page.padding,
          maxTop: Math.max(0, content.h - viewport.h),
        }),
        animated: true,
      })
    },
    [offsets, heights, viewport.h, content.h, design.page.padding],
  )

  /**
   * Keep the verdict in view while it is being laid out.
   *
   * The answer's own layout arrives *after* the result does — a verdict line, a
   * solution line and an error-tinted option row all still have to be measured — so
   * the first scroll is computed against the block's *old* height and stops short of
   * the end the reader is being taken to. Pinning re-runs the rule on every
   * re-measure for a moment, then lets go, so a later edit to the book cannot keep
   * dragging the reader down.
   */
  const pinnedVerdict = useRef<string | null>(null)
  const releasePinned = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pinVerdict = (id: string) => {
    pinnedVerdict.current = id
    if (releasePinned.current) clearTimeout(releasePinned.current)
    releasePinned.current = setTimeout(() => {
      pinnedVerdict.current = null
    }, 700)
    scrollToVerdict(id)
  }

  useEffect(() => {
    const id = pinnedVerdict.current
    if (id) scrollToVerdict(id)
  }, [heights, offsets, viewport.h, scrollToVerdict])

  /**
   * The page's last *answerable* block.
   *
   * A page can end with reading — a summary, a note, a plate — but a reader
   * finishes a page when they answer its last exercise (or mark its last passage
   * read), not when they reach the last thing printed on it. That block is the
   * one whose answer means "this page is done", so it is the one whose answer
   * should bring the page's own end into view.
   *
   * `-1` when the page prints nothing that can be answered at all: nothing
   * below fires, and the page turns exactly as it always did.
   */
  const lastAnswerableIndex = useMemo(() => {
    for (let i = blocks.length - 1; i >= 0; i -= 1) {
      if (SLIDE_TYPE_META[blocks[i].type]?.supported) return i
    }
    return -1
  }, [blocks])

  /**
   * Whether the page's scroller should keep following the content as it grows.
   *
   * A page-finish scroll is a promise about *where the bottom is*, and it is
   * made in the same frame as the answer that caused it: a verdict line, an
   * explanation and an error-tinted option row all still have to be laid out, so
   * the first scroll lands short of the end the reader is being taken to. This
   * keeps the scroller pinned to the end while that settles, then lets go —
   * otherwise a later edit to the book would keep dragging the reader down.
   */
  const followingEnd = useRef(false)
  const releaseFollow = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (releaseFollow.current) clearTimeout(releaseFollow.current)
      if (releasePinned.current) clearTimeout(releasePinned.current)
    },
    [],
  )

  /**
   * Take the reader to the end of the page.
   *
   * This is what stands in for the page turn a question cannot offer. The page
   * footer is furniture on the frame — it is always on screen — but on a page
   * longer than the window it is *disconnected* from what the reader is doing:
   * the last answer sits somewhere in the middle of the paper and the turn is
   * at the bottom of a screen they cannot see without scrolling. Bringing the
   * page's end up does both jobs at once: it shows that the page ends here, and
   * it puts the turn directly under the last thing they answered.
   */
  const scrollToPageEnd = () => {
    followingEnd.current = true
    scrollRef.current?.scrollToEnd({animated: true})
    if (releaseFollow.current) clearTimeout(releaseFollow.current)
    releaseFollow.current = setTimeout(() => {
      followingEnd.current = false
    }, 700)
  }

  /**
   * One rule per body text line. Squared paper keeps its own square size.
   */
  const step = design.page.textureStep ?? design.type.body.lineHeight
  const isRuled = design.block.mode === 'ruled'

  /**
   * The gap that brings a block's content back onto the ruling.
   *
   * An exercise book's lines are the point of the page, so the next exercise
   * has to start *on* a line — otherwise the ruling strikes through the first
   * word of every block for the rest of the page. The block is padded up to the
   * next line; only the ruled themes pay for it, and it is derived from the
   * theme's own pitch rather than from a magic number.
   */
  const gridPad = (id: string) => {
    if (!isRuled || step <= 0) return 0
    const h = heights[id]
    if (!h) return 0
    const rem = h % step
    return rem === 0 ? 0 : step - rem
  }

  const measureBlock = (id: string, h: number) => {
    setHeights(prev => (prev[id] === h ? prev : {...prev, [id]: h}))
  }

  return (
    <ScrollView
      ref={scrollRef}
      style={[styles.fill, styles.sheetContent]}
      contentContainerStyle={[
        styles.blockStack,
        {padding: design.page.padding},
      ]}
      showsVerticalScrollIndicator={false}
      bounces={false}
      onLayout={e => {
        const {width, height} = e.nativeEvent.layout
        setViewport(prev =>
          prev.w === width && prev.h === height ? prev : {w: width, h: height},
        )
      }}
      onScroll={e => {
        scrolled.current = e.nativeEvent.contentOffset.y
      }}
      scrollEventThrottle={16}
      onContentSizeChange={(w, h) => {
        setContent(prev => (prev.w === w && prev.h === h ? prev : {w, h}))
        // The answer's own layout arrives *after* the result does; see
        // `followingEnd`.
        if (followingEnd.current) {
          scrollRef.current?.scrollToEnd({animated: true})
        }
      }}>
      {/* The theme's ruling, drawn *inside* the scrolling content and at the
          text's own pitch, so the writing sits on the lines and the lines
          travel with it. */}
      <FlashBookPageRuling
        texture={design.page.texture}
        color={tokens.accent}
        width={content.w}
        height={content.h}
        step={step}
        offset={design.page.padding + step}
        inset={design.page.padding}
        rtl={direction === 'rtl'}
      />

      {blocks.map((block, i) => (
        <View
          key={block.id}
          onLayout={e => {
            const y = e.nativeEvent.layout.y
            setOffsets(prev =>
              prev[block.id] === y ? prev : {...prev, [block.id]: y},
            )
            if (!mounted.current[block.id]) {
              mounted.current[block.id] = true
              onBlockMount(block.id)
            }
          }}>
          {/* The theme's own section break — a flourished rule, a doubled
              editorial rule, a mission divider — never a generic card gap. */}
          {i > 0 &&
            (isRuled ? (
              // On a ruled page the break is exactly one line of the page: the
              // rhythm stays on the ruling instead of drifting off it.
              //
              // And it is *drawn like a line of the page*: the theme's own
              // `ruleOpacity` is tuned for a break printed on a plain sheet,
              // where it has to carry the separation on its own. Printed across
              // a ruling it becomes a painted band four times stronger than the
              // lines it is supposed to belong to — the one blue bar on the
              // page — so on ruled paper it takes the ruling's own strength.
              <View style={{height: step}}>
                <FlashBookDivider
                  divider={design.block.divider}
                  color={tokens.accent}
                  opacity={RULED_DIVIDER_OPACITY}
                  pad={step / 2 - 1}
                />
              </View>
            ) : (
              <FlashBookDivider
                divider={design.block.divider}
                color={tokens.cardBorder}
                opacity={design.block.ruleOpacity}
                pad={design.block.gap / 2}
              />
            ))}
          <View style={{paddingBottom: gridPad(block.id)}}>
            <View
              onLayout={e =>
                measureBlock(block.id, e.nativeEvent.layout.height)
              }>
              <FlashBookBlockPlayer
                block={block}
                tokens={tokens}
                design={design}
                pageDirection={direction}
                onResult={r => {
                  onBlockResult(block.id, block.type, r)
                  if (i === lastAnswerableIndex) {
                    // The page is finished. What the reader gets is the page's own
                    // end — where the page says it ends and where the turn is,
                    // with the last verdict printed just above it — rather than a
                    // view of the question they just answered and no sign of what
                    // to do next.
                    scrollToPageEnd()
                  } else {
                    // Answered, and the page is not finished: stay on this question.
                    // The verdict is printed under it, so the page moves only far
                    // enough to put that verdict on screen — never on to the next
                    // question, which is what used to scroll the answer away.
                    pinVerdict(block.id)
                  }
                }}
              />
            </View>
          </View>
        </View>
      ))}
      <View style={{height: 12}} />
    </ScrollView>
  )
}

/**
 * The book's progress language.
 *
 * Themes disagree about how progress reads — a filled bar, a run of page dots,
 * a measured editorial rule — and progress is visible on every single page, so
 * it carries more of a theme's identity than any other piece of chrome.
 */
function FlashBookProgress({
  style,
  pct,
  pageIndex,
  totalPages,
  accent,
  track,
  rtl = false,
}: {
  style: FlashBookDesign['progress']['style']
  pct: number
  pageIndex: number
  totalPages: number
  accent: string
  track: string
  /**
   * Whether the book is written right-to-left.
   *
   * A measure has a *beginning*, and in an Arabic book the beginning is the
   * right-hand end. Reading a progress bar that fills from the wrong side is
   * the clearest tell of a mirrored English interface: it says "you have come
   * this far" backwards.
   */
  rtl?: boolean
}) {
  if (style === 'dots') {
    return (
      <View style={[progress.dotRow, rtl && {direction: 'rtl' as const}]}>
        {Array.from({length: totalPages}, (_, i) => (
          <View
            key={i}
            style={[
              progress.dot,
              {
                backgroundColor: accent,
                opacity: i <= pageIndex ? 1 : 0.25,
                width: i === pageIndex ? 14 : 6,
              },
            ]}
          />
        ))}
      </View>
    )
  }
  // `rule` is a squared editorial measure; `bar` is the rounded default.
  const squared = style === 'rule'
  return (
    <View
      style={[
        styles.progressTrack,
        squared && progress.squaredTrack,
        // The track's own direction places the fill at its *start* edge, which
        // is the right one when the book runs right-to-left — so the measure
        // fills towards the reader's start rather than away from it.
        rtl && {direction: 'rtl' as const},
        {backgroundColor: track},
      ]}>
      <View
        style={[
          styles.progressFill,
          squared && {borderRadius: 0},
          {backgroundColor: accent, width: `${pct}%`},
        ]}
      />
    </View>
  )
}

const progress = StyleSheet.create({
  dotRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 3,
  },
  dot: {height: 6, borderRadius: 3},
  squaredTrack: {height: 2, borderRadius: 0},
})

const styles = StyleSheet.create({
  fill: {flex: 1},
  // Frame (9:16 portrait book, centered; full-bleed on phones)
  frameShell: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  frameShellFramed: {
    paddingVertical: 16,
  },
  // Inside `Layout.Center`, so the book is centred on the shell's centre column.
  frameColumn: {alignItems: 'center', justifyContent: 'center'},
  frame: {
    overflow: 'hidden',
  },
  frameRounded: {
    borderRadius: 24,
    borderWidth: 1,
    shadowColor: '#000',
    shadowOpacity: 0.5,
    shadowRadius: 24,
    shadowOffset: {width: 0, height: 8},
    elevation: 12,
  },
  // Cover (full-bleed art with the title set on it)
  coverFiller: {alignItems: 'center', justifyContent: 'center'},
  coverTopScrim: {position: 'absolute', top: 0, left: 0, right: 0, height: 140},
  coverBottomScrim: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: 380,
  },
  coverTop: {position: 'absolute', top: 0, left: 0, right: 0, padding: 20},
  coverBottom: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    padding: 20,
    gap: 8,
  },
  coverKicker: {
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    color: 'rgba(255,255,255,0.8)',
    textShadowColor: 'rgba(0,0,0,0.6)',
    textShadowOffset: {width: 0, height: 1},
    textShadowRadius: 8,
  },
  coverTitle: {
    fontSize: 30,
    fontWeight: '900',
    lineHeight: 36,
    color: '#fff',
    textShadowColor: 'rgba(0,0,0,0.6)',
    textShadowOffset: {width: 0, height: 1},
    textShadowRadius: 10,
  },
  coverDesc: {
    fontSize: 14,
    lineHeight: 21,
    color: 'rgba(255,255,255,0.88)',
    textShadowColor: 'rgba(0,0,0,0.6)',
    textShadowOffset: {width: 0, height: 1},
    textShadowRadius: 8,
  },
  coverAudioBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 13,
    borderRadius: 16,
    marginTop: 6,
  },
  coverAudioTxt: {color: '#fff', fontSize: 14, fontWeight: '700'},
  startBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 15,
    borderRadius: 18,
    marginTop: 8,
  },
  startTxt: {color: '#fff', fontSize: 16, fontWeight: '800'},
  /**
   * A quiet second action under a filled one: the same shape, an outline instead
   * of a fill, so "start over" is reachable without competing with "continue".
   */
  startBtnQuiet: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderRadius: 18,
    marginTop: 2,
    borderWidth: 1.5,
  },
  /**
   * What has been read of the book, set on the cover art and therefore carried by
   * the scrim rather than by a surface of its own.
   */
  coverHistory: {
    fontSize: 12,
    fontWeight: '700',
    color: 'rgba(255,255,255,0.85)',
    textShadowColor: 'rgba(0,0,0,0.6)',
    textShadowOffset: {width: 0, height: 1},
    textShadowRadius: 8,
  },
  // Reading header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 12,
    paddingTop: 8,
    paddingBottom: 8,
    // A printed rule under the running head, in the page's own hairline. The
    // header is part of the page, not an app bar sitting above it.
    borderBottomWidth: StyleSheet.hairlineWidth * 2,
    zIndex: 2,
  },
  headerBtn: {padding: 6},
  headerCenter: {flex: 1, gap: 6},
  headerTitle: {fontSize: 13, fontWeight: '700'},
  progressTrack: {height: 4, borderRadius: 2, overflow: 'hidden'},
  progressFill: {height: '100%', borderRadius: 2},
  finishBtn: {
    borderWidth: 1,
    borderRadius: 20,
    paddingHorizontal: 14,
    paddingVertical: 6,
  },
  finishTxt: {fontSize: 12, fontWeight: '700'},
  // Page
  // `minHeight: 0` is load-bearing on web: a flex child inherits `min-height:
  // auto` there, so without it a page refuses to shrink below the height of its
  // own content and hands its scroller a box taller than the book.
  page: {flex: 1, minHeight: 0},
  pageTop: {paddingTop: 6, paddingBottom: 6, gap: 2},
  pageTopRow: {flexDirection: 'row', alignItems: 'center', gap: 10},
  pageTitleFlex: {flex: 1},
  pageNumChip: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 20,
    borderWidth: 1,
  },
  pageSheet: {
    flex: 1,
    // See `page`: the sheet has to shrink to the frame so the page's scroller is
    // bounded — that is what makes a long page scroll instead of being clipped.
    minHeight: 0,
    overflow: 'hidden',
    zIndex: 1,
  },
  // Content sits above the theme's paper texture.
  sheetContent: {zIndex: 1},
  // Blocks carry their own themed padding; the stack only sets rhythm.
  blockStack: {},
  blockDivider: {alignItems: 'center', paddingVertical: 8, paddingTop: 16},
  blockDividerRule: {width: 44, height: 1, opacity: 0.75, borderRadius: 1},
  pageFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 14,
    gap: 10,
    borderTopWidth: StyleSheet.hairlineWidth * 2,
    zIndex: 2,
  },
  navBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    borderWidth: 1,
    borderRadius: 24,
    paddingHorizontal: 14,
    paddingVertical: 9,
  },
  navTxt: {fontSize: 13, fontWeight: '600'},
  nextBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderRadius: 24,
    paddingHorizontal: 18,
    paddingVertical: 10,
  },
  nextTxt: {color: '#fff', fontSize: 13, fontWeight: '800'},
})
